import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLocation } from 'wouter';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  location?: string;
  category: string;
  tags: string[];
  projectId?: string;
  isPrimary?: boolean;
}

interface GallerySectionProps {
  onImageClick?: (index: number, image: any) => void;
  onContactClick?: () => void;
}

// Define the Lightbox component
interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  image: GalleryImage | null;
}

const Lightbox: React.FC<LightboxProps> = ({ isOpen, onClose, image }) => {
  if (!isOpen || !image) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
        <button
          className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-20 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center"
          onClick={onClose}
        >
          &times;
        </button>
        <div className="relative max-w-full max-h-full flex items-center justify-center">
          <img 
            src={image.src} 
            alt={image.alt} 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent text-white rounded-b-lg">
            <h3 className="text-xl font-semibold mb-2">{image.title || image.alt}</h3>
            {image.description && (
              <p className="text-gray-200">{image.description}</p>
            )}
            {image.location && (
              <p className="text-gray-300 text-sm mt-1">📍 {image.location}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default function GallerySection({ onImageClick, onContactClick }: GallerySectionProps = {}) {
  const [, navigate] = useLocation();
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const openLightbox = (image: GalleryImage) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setLightboxOpen(false);
  };

  // Fetch gallery images from API
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch('/api/gallery');
        if (!response.ok) {
          throw new Error('Failed to fetch gallery images');
        }

        const data = await response.json();
        if (data.success) {
          setGalleryImages(data.data || []);
        } else {
          throw new Error(data.message || 'Failed to load gallery images');
        }
      } catch (error) {
        console.error('Error fetching gallery images:', error);
        setError('Failed to load gallery images.');
        setGalleryImages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  // Group images by project and get featured images (max 6 for homepage)
  const getFeaturedImages = () => {
    if (!galleryImages.length) return [];

    // Sort all images by ID descending (latest first) since newer uploads have higher IDs
    const sortedImages = [...galleryImages].sort((a, b) => b.id - a.id);

    // Group images by projectId, treating null/undefined as individual images
    const groupedImages = sortedImages.reduce((acc, image) => {
      const key = image.projectId || `single-${image.id}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(image);
      return acc;
    }, {} as Record<string, GalleryImage[]>);

    // Get primary images from each project, maintaining the latest-first order
    const projectKeys = Object.keys(groupedImages);
    const featuredImages = projectKeys
      .map(key => {
        const images = groupedImages[key];
        // Find primary image or use first image (which is now the latest due to sorting)
        return images.find(img => img.isPrimary) || images[0];
      })
      .slice(0, 6); // Limit to 6 images for homepage

    return featuredImages;
  };

  const featuredImages = getFeaturedImages();

  if (isLoading) {
    return (
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Work Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of exceptional welding projects
            </p>
          </div>
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full inline-block mb-2"></div>
            <p>Loading gallery...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Work Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of exceptional welding projects
            </p>
          </div>
          <div className="text-center py-8 text-red-600">
            <p className="mb-4">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Try Again
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (!featuredImages.length) {
    return (
      <section id="gallery" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Work Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of exceptional welding projects
            </p>
          </div>
          <div className="text-center py-8 text-gray-500">
            <p>No gallery images available at the moment.</p>
            <p className="text-sm mt-2">Check back soon for updates!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Work Gallery</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our portfolio of exceptional welding projects showcasing our craftsmanship and attention to detail
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {featuredImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-0">
                  <div className="relative aspect-[4/3] overflow-hidden group">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        console.error('Failed to load image:', image.src);
                        const target = e.currentTarget;

                        // Prevent infinite retry loops
                        if (target.dataset.retryAttempt) {
                          // Final fallback to placeholder
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                          return;
                        }

                        // Mark retry attempt
                        target.dataset.retryAttempt = 'true';

                        // Try fallback to uploads folder
                        const filename = image.src.split('/').pop();
                        if (filename) {
                          target.src = `/uploads/${filename}`;
                        } else {
                          // Fallback to placeholder
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXpePSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Button 
                        variant="secondary"
                        className="bg-white text-black hover:bg-gray-100 mr-2"
                        onClick={() => openLightbox(image)}
                      >
                        Quick View
                      </Button>
                      <Button 
                        variant="secondary"
                        className="bg-white text-black hover:bg-gray-100"
                        onClick={() => navigate('/gallery')}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-lg mb-2">
                      {image.title || 'Professional Welding Project'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-2">
                      {image.category.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </p>
                    {image.location && (
                      <p className="text-gray-500 text-sm">
                        📍 {image.location}
                      </p>
                    )}
                    {image.description && (
                      <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                        {image.description}
                      </p>
                    )}
                    {image.tags && image.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {image.tags.slice(0, 2).map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {image.tags.length > 2 && (
                          <span className="text-xs text-gray-400">
                            +{image.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Button 
            size="lg"
            onClick={() => navigate('/gallery')}
            className="bg-black/50 hover:bg-black/70 text-white px-8 py-3"
          >
            View Full Gallery
          </Button>
        </div>
      </div>

      {/* Lightbox for quick view */}
      <Lightbox
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        image={selectedImage}
      />
    </section>
  );
}